import React, { ChangeEvent, memo } from 'react';
import { useTranslation } from 'i18n';
import { useSelector } from 'react-redux';
import { clearValueParser, stringValueFormatter } from './utils';
import FilterSelectHandler from './FilterSelectHandler';
import { makeEntitiesLoader } from '../../hooks/makeSimpleLoader';
import { MarketsActions } from '../../store/actions';
import { DetectMarketSelectors, MarketsSelectors } from '../../store/selectors';
import { MARKETS_SLUGS_MAP } from '../../utils/transform';

export interface IPriceFilterProps {
  disabled?: boolean;
  hideAllMarkets?: boolean;
}

const ITEM_ALL = 'all';
const ITEM_LOCAL = 'local';
const ITEM_NON_LOCAL = 'nonLocal';

const items = [ITEM_NON_LOCAL, ITEM_LOCAL, ITEM_ALL];
const itemsShort = [ITEM_NON_LOCAL];

const params = {
  page_size: 100,
};

const useMarketsState = makeEntitiesLoader(MarketsActions.getMarkets, MarketsSelectors.marketsState);

const LocalMarketFilter: React.FC<React.PropsWithChildren<IPriceFilterProps>> = (props) => {
  const { disabled, hideAllMarkets } = props;
  const [t] = useTranslation();
  const itemsToShow = hideAllMarkets ? itemsShort : items;
  const marketsState = useMarketsState(() => params, []);
  const market = useSelector(DetectMarketSelectors.getSelectedMarket);

  const marketValueTransformer = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => {
    if (value === ITEM_ALL) {
      return { [name]: undefined };
    }
    if (value === ITEM_LOCAL) {
      return { [name]: market };
    }
    return { [name]: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP] };
  };

  const availableOptions = () => {
    if (market === 'international') return itemsToShow.slice(1, itemsToShow.length);
    return itemsToShow;
  };

  const marketValueParser = (queryParams: Record<string, string | string[]>, name: string) => {
    const value = queryParams[name];
    if (!value) {
      return ITEM_ALL;
    }
    if (Array.isArray(value)) {
      return ITEM_NON_LOCAL;
    }
    return ITEM_LOCAL;
  };

  return (
    <FilterSelectHandler
      name="market_slug"
      placeholder={t<string>('common:filter.city')}
      disabled={disabled || marketsState.isFetching}
      items={availableOptions()}
      labelFormatter={(item) => t(`common:general.localMarket_${item}`)}
      valueFormatter={stringValueFormatter}
      valueTransformer={marketValueTransformer}
      valueParser={marketValueParser}
      clearValueParser={clearValueParser}
    />
  );
};

export default memo(LocalMarketFilter);
